// frontend/src/contexts/ThemeContext.js - Centralized theme management
import { createContext, useContext, useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import { usePreferences } from './PreferenceContext';
import logger from '../utils/logger';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Professional color palette with comprehensive design tokens
const themes = {
  light: {
    // Core backgrounds - clean, professional whites and grays
    background: {
      primary: 'bg-white',           // Main content areas
      secondary: 'bg-gray-50',       // Subtle sections
      tertiary: 'bg-gray-100',       // Nested content
      elevated: 'bg-white',          // Cards, modals
      inverse: 'bg-gray-900',        // Inverted sections
      overlay: 'bg-black/50',        // Modal overlays
    },
    
    // Typography - professional gray scale
    text: {
      primary: 'text-gray-900',      // Headings, important text
      secondary: 'text-gray-700',    // Body text
      muted: 'text-gray-500',        // Helper text
      disabled: 'text-gray-400',     // Disabled state
      inverse: 'text-white',         // On dark backgrounds
      link: 'text-blue-600',         // Links
      linkHover: 'hover:text-blue-700',
    },
    
    // Borders - subtle and clean
    border: {
      default: 'border-gray-200',    // Default borders
      strong: 'border-gray-300',     // Input borders
      subtle: 'border-gray-100',     // Subtle dividers
      focus: 'border-blue-500',      // Focus state
      error: 'border-red-500',       // Error state
    },
    
    // Semantic colors - professional palette
    semantic: {
      primary: 'bg-blue-600 text-white',
      primaryHover: 'hover:bg-blue-700',
      secondary: 'bg-gray-100 text-gray-700',
      secondaryHover: 'hover:bg-gray-200',
      success: 'bg-emerald-50 text-emerald-700',
      successStrong: 'bg-emerald-600 text-white',
      error: 'bg-red-50 text-red-700',
      errorStrong: 'bg-red-600 text-white',
      warning: 'bg-amber-50 text-amber-700',
      warningStrong: 'bg-amber-600 text-white',
      info: 'bg-blue-50 text-blue-700',
      infoStrong: 'bg-blue-600 text-white',
    },
    
    // Component-specific styles
    components: {
      // Buttons - professional and elegant
      button: {
        primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
        secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
        danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
        ghost: 'text-gray-700 hover:bg-gray-100 focus:ring-2 focus:ring-gray-500',
        disabled: 'bg-gray-200 text-gray-400 cursor-not-allowed',
      },
      
      // Form inputs - clean and functional
      input: {
        base: 'border-gray-300 bg-white text-gray-900 placeholder-gray-400 hover:border-gray-400 hover:bg-gray-50',
        focus: 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
        error: 'border-red-500 focus:ring-2 focus:ring-red-500',
        disabled: 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed',
      },
      
      // Cards - subtle elevation
      card: {
        base: 'bg-white border border-gray-200 rounded-lg shadow-sm',
        hover: 'hover:shadow-md transition-shadow',
        selected: 'ring-2 ring-blue-500',
      },
      
      // Navigation - clean and minimal
      navigation: {
        item: 'text-gray-700 hover:text-gray-900 hover:bg-gray-100',
        active: 'text-blue-600 bg-blue-50',
        disabled: 'text-gray-400 cursor-not-allowed',
      },
      
      // Dropdowns and menus
      dropdown: {
        base: 'bg-white border border-gray-200 shadow-lg',
        item: 'text-gray-700 hover:bg-gray-100',
        active: 'bg-blue-50 text-blue-700',
      },
      
      // Badges and pills
      badge: {
        default: 'bg-gray-100 text-gray-700',
        primary: 'bg-blue-100 text-blue-700',
        success: 'bg-emerald-100 text-emerald-700',
        error: 'bg-red-100 text-red-700',
        warning: 'bg-amber-100 text-amber-700',
      },
    },
    
    // Interactive states
    states: {
      hover: {
        opacity: 'hover:opacity-90',
        scale: 'hover:scale-[1.02]',
        transition: 'transition-all duration-200',
      },
      active: {
        scale: 'active:scale-[0.98]',
      },
      focus: {
        ring: 'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
        outline: 'focus:outline-none',
      },
      disabled: {
        opacity: 'disabled:opacity-50',
        cursor: 'disabled:cursor-not-allowed',
      },
    },
    
    // Shadows - subtle depth
    shadow: {
      sm: 'shadow-sm',
      DEFAULT: 'shadow',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
      inner: 'shadow-inner',
      none: 'shadow-none',
    },
    
    // Raw colors for edge cases
    colors: {
      primary: '#3B82F6',
      primaryDark: '#2563EB',
      secondary: '#6B7280',
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
      info: '#3B82F6',
      background: '#FFFFFF',
      surface: '#F9FAFB',
      text: '#111827',
      textSecondary: '#6B7280',
      border: '#E5E7EB',
      // Map-specific colors for polylines
      map: {
        routeSelected: '#3B82F6',       // Blue - selected route
        routeAlternative: '#7C3AED',    // Purple - alternative variants (high contrast)
        routeCompleted: '#22C55E',      // Green - completed progress
        routeRemaining: '#3B82F6',      // Blue - remaining route
        routeScheduled: '#6B7280',      // Gray - scheduled trips
        routeDispatched: '#EAB308',     // Yellow - dispatched trips
        routeInProgress: '#3B82F6',     // Blue - in progress trips
        routeCancelled: '#EF4444',      // Red - cancelled trips
      },
    }
  },
  dark: {
    // Dark theme - professional and high contrast
    background: {
      primary: 'bg-gray-900',        // Main background
      secondary: 'bg-gray-800',      // Cards, elevated surfaces
      tertiary: 'bg-gray-700',       // Nested content
      elevated: 'bg-gray-800',       // Cards, modals
      inverse: 'bg-white',           // Inverted sections
      overlay: 'bg-black/70',        // Modal overlays
    },
    
    text: {
      primary: 'text-gray-100',      // Headings
      secondary: 'text-gray-300',    // Body text
      muted: 'text-gray-400',        // Helper text
      disabled: 'text-gray-500',     // Disabled state
      inverse: 'text-gray-900',      // On light backgrounds
      link: 'text-blue-400',         // Links
      linkHover: 'hover:text-blue-300',
    },
    
    border: {
      default: 'border-gray-700',    // Default borders
      strong: 'border-gray-600',     // Input borders
      subtle: 'border-gray-800',     // Subtle dividers
      focus: 'border-blue-400',      // Focus state
      error: 'border-red-400',       // Error state
    },
    
    semantic: {
      primary: 'bg-blue-600 text-white',
      primaryHover: 'hover:bg-blue-500',
      secondary: 'bg-gray-700 text-gray-200',
      secondaryHover: 'hover:bg-gray-600',
      success: 'bg-emerald-900/20 text-emerald-400',
      successStrong: 'bg-emerald-600 text-white',
      error: 'bg-red-900/20 text-red-400',
      errorStrong: 'bg-red-600 text-white',
      warning: 'bg-amber-900/20 text-amber-400',
      warningStrong: 'bg-amber-600 text-white',
      info: 'bg-blue-900/20 text-blue-400',
      infoStrong: 'bg-blue-600 text-white',
    },
    
    components: {
      button: {
        primary: 'bg-blue-600 text-white hover:bg-blue-500 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900',
        secondary: 'bg-gray-600 text-white border border-gray-500 hover:bg-gray-500 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-gray-900',
        danger: 'bg-red-600 text-white hover:bg-red-500 focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-gray-900',
        ghost: 'text-gray-200 hover:bg-gray-700 focus:ring-2 focus:ring-gray-400',
        disabled: 'bg-gray-700 text-gray-500 cursor-not-allowed',
      },
      
      input: {
        base: 'border-gray-600 bg-gray-800 text-gray-100 placeholder-gray-400 hover:border-gray-400 hover:bg-gray-700',
        focus: 'focus:ring-2 focus:ring-blue-400 focus:border-blue-400',
        error: 'border-red-400 focus:ring-2 focus:ring-red-400',
        disabled: 'border-gray-700 bg-gray-700 text-gray-500 cursor-not-allowed',
      },
      
      card: {
        base: 'bg-gray-800 border border-gray-700 rounded-lg shadow-xl',
        hover: 'hover:shadow-2xl transition-shadow',
        selected: 'ring-2 ring-blue-400',
      },
      
      navigation: {
        item: 'text-gray-300 hover:text-gray-100 hover:bg-gray-700',
        active: 'text-blue-400 bg-gray-700',
        disabled: 'text-gray-500 cursor-not-allowed',
      },
      
      dropdown: {
        base: 'bg-gray-800 border border-gray-700 shadow-xl',
        item: 'text-gray-300 hover:bg-gray-700',
        active: 'bg-gray-700 text-blue-400',
      },
      
      badge: {
        default: 'bg-gray-700 text-gray-300',
        primary: 'bg-blue-900/50 text-blue-400',
        success: 'bg-emerald-900/50 text-emerald-400',
        error: 'bg-red-900/50 text-red-400',
        warning: 'bg-amber-900/50 text-amber-400',
      },
    },
    
    states: {
      hover: {
        opacity: 'hover:opacity-90',
        scale: 'hover:scale-[1.02]',
        transition: 'transition-all duration-200',
      },
      active: {
        scale: 'active:scale-[0.98]',
      },
      focus: {
        ring: 'focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900',
        outline: 'focus:outline-none',
      },
      disabled: {
        opacity: 'disabled:opacity-50',
        cursor: 'disabled:cursor-not-allowed',
      },
    },
    
    shadow: {
      sm: 'shadow-sm shadow-black/50',
      DEFAULT: 'shadow shadow-black/50',
      md: 'shadow-md shadow-black/50',
      lg: 'shadow-lg shadow-black/50',
      xl: 'shadow-xl shadow-black/50',
      inner: 'shadow-inner shadow-black/50',
      none: 'shadow-none',
    },
    
    colors: {
      primary: '#60A5FA',
      primaryDark: '#3B82F6',
      secondary: '#9CA3AF',
      success: '#34D399',
      error: '#F87171',
      warning: '#FBBF24',
      info: '#60A5FA',
      background: '#111827',
      surface: '#1F2937',
      text: '#F9FAFB',
      textSecondary: '#D1D5DB',
      border: '#374151',
      // Map-specific colors for polylines (brighter for dark mode visibility)
      map: {
        routeSelected: '#60A5FA',       // Lighter blue - selected route
        routeAlternative: '#A78BFA',    // Lighter purple - alternative variants
        routeCompleted: '#34D399',      // Lighter green - completed progress
        routeRemaining: '#60A5FA',      // Lighter blue - remaining route
        routeScheduled: '#9CA3AF',      // Lighter gray - scheduled trips
        routeDispatched: '#FBBF24',     // Brighter yellow - dispatched trips
        routeInProgress: '#60A5FA',     // Lighter blue - in progress trips
        routeCancelled: '#F87171',      // Lighter red - cancelled trips
      },
    }
  }
};

const COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const getSystemPrefersDark = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia(COLOR_SCHEME_QUERY).matches;
};

const subscribeToSystemColorScheme = (onStoreChange) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }

  const mediaQuery = window.matchMedia(COLOR_SCHEME_QUERY);

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', onStoreChange);
    return () => mediaQuery.removeEventListener('change', onStoreChange);
  }

  mediaQuery.addListener(onStoreChange);
  return () => mediaQuery.removeListener(onStoreChange);
};

const resolveThemeName = (themeName, systemPrefersDark = false) => {
  if (themeName === 'auto') {
    return systemPrefersDark ? 'dark' : 'light';
  }

  return themeName && themes[themeName] ? themeName : 'light';
};

export const ThemeProvider = ({ children }) => {
  // Pull live preferences from PreferenceContext (single source of truth)
  const { preferences } = usePreferences();

  // Initialize theme from EAV cache only
  const getInitialTheme = () => {
    try {
      const cached = localStorage.getItem('cachedPreferences');
      if (cached) {
        const parsed = JSON.parse(cached);
        const fromCache = parsed?.userGlobal?.theme;
        if (fromCache) return fromCache;
      }
    } catch (error) {
      logger.error('[ThemeContext] Error resolving initial theme', error);
    }
    return 'light';
  };

  const [selectedTheme, setSelectedTheme] = useState(getInitialTheme());
  const selectedThemeName = preferences?.userGlobal?.theme || selectedTheme;
  const subscribeToSelectedSystemTheme = useCallback((onStoreChange) => {
    if (selectedThemeName !== 'auto') {
      return () => {};
    }

    // Auto theme is authority from the user's preference, so subscribe only
    // while it is selected and let React read the current OS color snapshot.
    return subscribeToSystemColorScheme(onStoreChange);
  }, [selectedThemeName]);
  const systemPrefersDark = useSyncExternalStore(
    subscribeToSelectedSystemTheme,
    getSystemPrefersDark,
    () => false
  );
  const theme = resolveThemeName(selectedThemeName, systemPrefersDark);
  const currentTheme = themes[theme] || themes.light;

  const applyTheme = useCallback((themeName) => {
    setSelectedTheme(themeName || 'light');
  }, []);

  // Keep document `dark` class in sync on mount and whenever theme changes
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Helper function to get theme classes
  const getThemeClass = useCallback((path) => {
    const keys = path.split('.');
    let value = currentTheme;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        logger.warn('Theme path not found', { path });
        return '';
      }
    }
    
    return value || '';
  }, [currentTheme]);

  // Helper function to combine multiple theme classes
  const cn = useCallback((...classes) => {
    return classes.filter(Boolean).join(' ');
  }, []);

  // Helper function to get color value (for inline styles)
  const getColor = useCallback((colorKey) => {
    return currentTheme.colors[colorKey] || '';
  }, [currentTheme]);

  const value = {
    theme,
    currentTheme,
    themes,
    applyTheme,
    getThemeClass,
    cn,
    getColor,
    // Expose commonly used theme values directly
    background: currentTheme.background,
    text: currentTheme.text,
    border: currentTheme.border,
    semantic: currentTheme.semantic,
    components: currentTheme.components,
    states: currentTheme.states,
    shadow: currentTheme.shadow,
    colors: currentTheme.colors,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
